import { useState, useEffect, useRef } from 'react';
import { ChevronRight, Menu, X, ArrowRight } from 'lucide-react';
import ContactPopup from './ContactPopup';
import ChatBotIcon from './ChatBotIcon';



// Header Component
const Header = ({ openContactPopup }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black bg-opacity-90 py-2' : 'bg-transparent py-4'}`}>
      <div className="container mx-auto px-4 flex justify-between items-center">
        <div className="flex items-center">
          {/* Logo */}
          <div className="flex items-center">
            <svg 
              width="40" 
              height="32" 
              viewBox="0 0 100 80" 
              className="fill-current text-red-600"
            >
              <path d="M50,0 L95,60 L80,60 L50,20 L20,60 L5,60 L50,0 Z M40,70 L60,70 L60,80 L40,80 L40,70 Z" />
            </svg>
            <span className="ml-2 text-xl font-bold text-white">PHOEN<span className="text-red-600">AI</span>X</span>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <a href="#projects" className="text-white hover:text-red-500 transition-colors">Projects</a>
          <a href="#about" className="text-white hover:text-red-500 transition-colors">About</a>
          <a href="solutions" className="text-white hover:text-red-500 transition-colors">Solutions</a>
          <button 
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
            onClick={openContactPopup}
          >
            Contact Us
          </button>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-black bg-opacity-95 absolute top-full left-0 right-0 p-4">
          <nav className="flex flex-col space-y-4">
            <a href="#projects" className="text-white hover:text-red-500 transition-colors py-2 border-b border-gray-800" onClick={() => setIsMenuOpen(false)}>Projects</a>
            <a href="#about" className="text-white hover:text-red-500 transition-colors py-2 border-b border-gray-800" onClick={() => setIsMenuOpen(false)}>About</a>
            <a href="solutions" className="text-white hover:text-red-500 transition-colors py-2 border-b border-gray-800" onClick={() => setIsMenuOpen(false)}>Solutions</a>
            <a href="#contact" className="text-white hover:text-red-500 transition-colors py-2 border-b border-gray-800" onClick={() => setIsMenuOpen(false)}>Contact</a>
            <button 
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
              onClick={() => {
                setIsMenuOpen(false);
                openContactPopup();
              }}
            >
              Get Started
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section className="relative h-screen bg-black flex items-center">
      {/* Background gradient */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-black via-black to-red-900 opacity-40"
        style={{ 
          backgroundImage: 'radial-gradient(circle at top right, rgba(224, 31, 38, 0.2), transparent 70%)' 
        }}
      />
      
      <div className="container mx-auto px-4 z-10">
        <div className="max-w-3xl">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">The Age of <span className="text-red-600">AI</span> Is Here</h1>
          <p className="text-xl text-gray-300 mb-8">
            PHOENAIX builds next-generation AI agents and applications that transform how businesses operate and make decisions.
          </p>
          <div className="flex flex-wrap gap-4">
          <a
            href="/solutions"
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded flex items-center"
          >
            Explore Solutions <ChevronRight className="ml-2" size={20} />
          </a>
          <a
            href="/#projects"
            className="border border-white text-white hover:bg-white hover:text-black transition-colors px-6 py-3 rounded"
          >
            Learn More
          </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Project Card Component
const ProjectCard = ({ project, onClick }) => {
  return (
    <div 
      className="group bg-gray-900 rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-red-900/20 cursor-pointer" 
      style={{ height: '400px' }}
      onClick={() => onClick(project.id)}
    >
      <div className="h-1/2 overflow-hidden">
        <img 
          src={project.image} 
          alt={project.title} 
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-6 h-1/2 flex flex-col justify-between">
        <div>
          <span className="text-red-500 text-sm font-medium mb-2 block">{project.category}</span>
          <h3 className="text-xl font-bold text-white mb-2">{project.title}</h3>
          <p className="text-gray-400">{project.description}</p>
        </div>
        <div className="mt-4">
          <button 
            className="inline-flex items-center text-red-500 hover:text-red-400 font-medium"
          >
            View Product <ArrowRight size={16} className="ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Projects Section with Filtering
const ProjectsSection = ({ projects, onProjectClick }) => {
  const CATEGORIES = ['All', 'Enterprise', 'Retrieval-Augmented Generation', 'Analytics', 'Computer Vision', 'Autonomous Systems', 'Edge Computing'];
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filteredProjects, setFilteredProjects] = useState(projects);

  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(project => project.category === selectedCategory));
    }
  }, [selectedCategory, projects]);

  return (
    <section id="projects" className="py-20 bg-black">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">Our <span className="text-red-600">Projects</span></h2>
        
        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {CATEGORIES.map((category) => (
            <button
              key={category}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                selectedCategory === category 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
        
        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <ProjectCard 
              key={project.id} 
              project={project} 
              onClick={onProjectClick}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-20 bg-gradient-to-b from-black to-gray-900">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <div className="md:w-1/2">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">About <span className="text-red-600">PHOENAIX</span></h2>
            <p className="text-gray-300 mb-6">
              Founded in 2025, PHOENAIX is at the forefront of artificial intelligence innovation. 
              Our mission is to create intelligent systems that augment human capabilities and solve 
              complex problems across industries.
            </p>
            <p className="text-gray-300 mb-6">
              With a team of world-class researchers, engineers, and domain experts, we develop 
              cutting-edge AI solutions that are reliable, ethical, and transformative.
            </p>
          </div>
          <div className="md:w-1/2">
            <div className="relative">
              <div className="absolute -inset-4 bg-red-600 opacity-10 rounded-lg blur-xl"></div>
              <div className="relative bg-gray-800 p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-white mb-4">Our Values</h3>
                <ul className="space-y-4">
                  {['Innovation', 'Integrity', 'Human-centered AI', 'Excellence'].map((value) => (
                    <li key={value} className="flex items-start">
                      <div className="mt-1 mr-3 flex-shrink-0 w-5 h-5 rounded-full bg-red-600 flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">{value}</h4>
                        <p className="text-gray-400">
                          {value === 'Innovation' && 'Pushing boundaries with cutting-edge research and development.'}
                          {value === 'Integrity' && 'Building trust through transparent and ethical AI practices.'}
                          {value === 'Human-centered AI' && 'Creating technology that empowers and augments human capabilities.'}
                          {value === 'Excellence' && 'Striving for exceptional quality in all our solutions.'}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = ({ openContactPopup }) => {
  return (
    <footer id="contact" className="bg-black text-white py-12">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between mb-12">
          <div className="mb-8 md:mb-0">
            <div className="flex items-center mb-4">
              <svg 
                width="32" 
                height="24" 
                viewBox="0 0 100 80" 
                className="fill-current text-red-600"
              >
                <path d="M50,0 L95,60 L80,60 L50,20 L20,60 L5,60 L50,0 Z M40,70 L60,70 L60,80 L40,80 L40,70 Z" />
              </svg>
              <span className="ml-2 text-xl font-bold">PHOEN<span className="text-red-600">AI</span>X</span>
            </div>
            <p className="text-gray-400 max-w-md">
              Transforming industries through innovative AI solutions. 
              Our intelligent agents and applications power the future of business.
            </p>
          </div>
          
          <div className="w-full flex flex-col items-end">
  <div className="bg-gray-800 p-4 rounded-lg shadow-lg mt-auto">
    <h3 className="text-lg font-semibold mb-4 text-white">Contact Us</h3>
    <ul className="space-y-2">
      <li><a href="tel:+917200957597" className="text-gray-400 hover:text-red-500 transition-colors">+91 72009 57597</a></li>
      <li><a href="mailto:phoenaixworkspace@gmail.com" className="text-gray-400 hover:text-red-500 transition-colors">phoenaixworkspace@gmail.com</a></li>
      <li>
        <button 
          onClick={openContactPopup} 
          className="text-gray-400 hover:text-red-500 transition-colors"
        >
          Send us a message
        </button>
      </li>
    </ul>
  </div>
</div>
        </div>
        
        <div className="pt-8 border-t border-gray-800 text-center md:text-left flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 text-sm">© 2025 PHOENAIX. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <a href="#" className="text-gray-400 hover:text-red-500 transition-colors">Privacy Policy</a>
            <a href="#" className="text-gray-400 hover:text-red-500 transition-colors">Terms of Service</a>
            <a href="#" className="text-gray-400 hover:text-red-500 transition-colors">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// HomePage Component
export default function HomePage({ projects, onProjectClick }) {
  const [isContactPopupOpen, setIsContactPopupOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const messageHandlerRef = useRef(null);

  const handleChatToggle = () => {
    setIsChatOpen(prev => !prev);
  };
  const openContactPopup = () => {
    setIsContactPopupOpen(true);
  };
  
  const closeContactPopup = () => {
    setIsContactPopupOpen(false);
  };
  
  useEffect(() => {
    document.body.style.backgroundColor = '#000000';
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Header openContactPopup={openContactPopup} />
      <Hero />
      <ProjectsSection projects={projects} onProjectClick={onProjectClick} />
      <AboutSection />
      <Footer openContactPopup={openContactPopup} />
      <ContactPopup isOpen={isContactPopupOpen} onClose={closeContactPopup} />
      <ChatBotIcon 
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        onToggle={handleChatToggle}
        onExternalMessage={(handler) => messageHandlerRef.current = handler} 
      />
    </div>
  );
}